/**
 * Image Preloader Utility
 * Dynamically adds preload hints for critical images (like LCP images)
 */

// Track preloaded images to prevent duplicates
const preloadedImages = new Set()

/**
 * Adds a preload link for an image to the document head
 * @param {string} imageUrl - Full URL of the image to preload
 * @param {string} as - Resource type ('image' by default)
 * @param {string} fetchPriority - Priority hint: 'high' | 'low' | 'auto' (default: 'high')
 * @param {string} media - Media query for responsive images (e.g., '(orientation: landscape)')
 * @returns {HTMLLinkElement} The created link element
 */
export function preloadImage(imageUrl, { as = 'image', fetchPriority = 'high', media = null } = {}) {
  // Check if already preloaded
  if (preloadedImages.has(imageUrl)) {
    return null
  }

  // Check if link already exists
  const existingLink = document.querySelector(`link[rel="preload"][href="${imageUrl}"]`)
  if (existingLink) {
    preloadedImages.add(imageUrl)
    return existingLink
  }

  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = as
  link.href = imageUrl
  link.fetchPriority = fetchPriority
  
  if (media) {
    link.media = media
  }

  document.head.appendChild(link)
  preloadedImages.add(imageUrl)

  return link
}

/**
 * Preloads hero card images for LCP optimization
 * Preloads both landscape and portrait with appropriate media queries
 * @param {string} landscapeImageUrl - Landscape image URL
 * @param {string} portraitImageUrl - Portrait image URL
 */
export function preloadHeroCardImages(landscapeImageUrl, portraitImageUrl) {
  if (landscapeImageUrl) {
    preloadImage(landscapeImageUrl, {
      as: 'image',
      fetchPriority: 'high',
      media: '(orientation: landscape)',
    })
  }

  if (portraitImageUrl) {
    preloadImage(portraitImageUrl, {
      as: 'image',
      fetchPriority: 'high',
      media: '(orientation: portrait)',
    })
  }
}

/**
 * Removes a preload link (for cleanup if needed)
 * @param {string} imageUrl - URL of the image to remove preload for
 */
export function removePreload(imageUrl) {
  const link = document.querySelector(`link[rel="preload"][href="${imageUrl}"]`)
  if (link) {
    link.remove()
    preloadedImages.delete(imageUrl)
  }
}

